import axios from "axios";
import { getClientError, getServerError } from "messages";
import qs from "qs";
import { getApiUrl } from "services";
import { FieldErrorResponse } from "./FieldErrorResponse";

export const RestClient = axios.create({
  baseURL: getApiUrl(),
  validateStatus: (status) => (200 <= status && status < 300) || status === 404,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
  responseType: "json",
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
  withCredentials: true,
  httpsAgent: { keepAlive: true },
});

RestClient.prototype.prog = false;
RestClient.prototype.setProg = null;

RestClient.interceptors.request.use(
  (value) => {
    changeProgRest(true);
    return value;
  },
  (error) => {
    changeProgRest(false);
    throw error;
  }
);
RestClient.interceptors.response.use(
  (value) => {
    changeProgRest(false);
    return value;
  },
  (error) => {
    changeProgRest(false);
    throw error;
  }
);

export const changeProgRest = (on: boolean) => {
  if (RestClient.prototype.setProg && RestClient.prototype.prog !== on) {
    RestClient.prototype.setProg(on);
  }
};

export const catchRestError = (
  error: any,
  navi: Function,
  setMsg: Function,
  fieldNames: any = null
) => {
  changeProgRest(false);
  if (serverError(error, setMsg)) return;
  if (authError(error, setMsg, navi)) return;
  if (fieldInvalidError(error, setMsg, fieldNames)) return;
  if (invalidError(error, setMsg)) return;
};

export const FileClient = axios.create({
  baseURL: getApiUrl(),
  validateStatus: (status) => (200 <= status && status < 300) || status === 404,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
  responseType: "arraybuffer",
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
  withCredentials: true,
  httpsAgent: { keepAlive: true },
});

FileClient.prototype.prog = false;
FileClient.prototype.setProg = null;

FileClient.interceptors.request.use(
  (value) => {
    changeFileProg(true);
    return value;
  },
  (error) => {
    changeFileProg(false);
    throw error;
  }
);
FileClient.interceptors.response.use(
  (value) => {
    changeFileProg(false);
    return value;
  },
  (error) => {
    changeFileProg(false);
    throw error;
  }
);

const changeFileProg = (on: boolean) => {
  if (FileClient.prototype.setProg && FileClient.prototype.prog !== on) {
    FileClient.prototype.setProg(on);
  }
};

export const catchFileError = (
  error: any,
  navi: Function,
  setMsg: Function,
  fieldNames: any = null
) => {
  changeFileProg(false);
  if (serverError(error, setMsg)) return;
  if (authError(error, setMsg, navi)) return;
  if (fieldInvalidFileError(error, setMsg, fieldNames)) return;
  if (invalidFileError(error, setMsg)) return;
};

/**
 * ファイルアップロード用フォームデータ変換
 *
 * - アップロードする場合はRequestをフォームデータに変換する。
 * - RequestはFileインターフェースかstringのみ定義可能とする。
 *
 * @param params
 * @returns
 */
export const getFormData = (params: any) => {
  const formData = new FormData();
  Object.keys(params).forEach((key) => {
    if (!(key in params)) return;
    const value = params[key];
    if (value instanceof File) {
      formData.append(key, value as Blob, value.name);
    } else if (typeof value === "string") {
      formData.append(key, value);
    }
  });
  return formData;
};

export const setProgressState = (prog: boolean, setProg: Function) => {
  RestClient.prototype.prog = prog;
  RestClient.prototype.setProg = setProg;
  FileClient.prototype.prog = prog;
  FileClient.prototype.setProg = setProg;
};

const serverError = (error: any, setMsg: Function): boolean => {
  if (!error) {
    setMsg(getServerError("S000200_001"));
    return true;
  }
  if (!error.response) {
    if (error.message === "Network Error") {
      setMsg(getServerError("S000200_002"));
    } else {
      setMsg(getServerError("S000200_001"));
    }
    return true;
  }
  return false;
};
const authError = (error: any, setMsg: Function, navi: Function): boolean => {
  if (error && error.response && error.response.status === 401) {
    setMsg(getClientError("B000010_001"));
    navi("/b000010");
    return true;
  }
  return false;
};
const fieldInvalidError = (
  error: any,
  setMsg: Function,
  fieldNames: any
): boolean => {
  if (fieldNames === null) return false;
  if (!error.response.data) {
    setMsg(getServerError("S000200_001"));
    return true;
  }
  if (!error.response.data.key) {
    return false;
  }
  const fe = error.response.data as FieldErrorResponse;
  let param = "";
  if (fe.meisaiKey) {
    const meisai = fieldNames[fe.meisaiKey];
    param += `${meisai[fe.meisaiKey]}${fe.meisaiNo}行目`;
    if (fe.koseiKey) {
      const kosei = fieldNames[fe.meisaiKey][fe.koseiKey];
      param += ` ${kosei[fe.koseiKey]}${fe.koseiNo}行目 ${kosei[fe.key]}`;
    } else {
      param += ` ${meisai[fe.key]}`;
    }
  } else {
    param += `${fieldNames[fe.key]}`;
  }
  setMsg(getServerError(fe.errorCode, param));
  return true;
};
const invalidError = (error: any, setMsg: Function): boolean => {
  if (!error.response.data || !error.response.data.errorCode) {
    setMsg(getServerError("S000200_001"));
    return true;
  }
  const errorCode = error.response.data.errorCode;
  const parameters = error.response.data.parameters;
  if (parameters && parameters.length > 0) {
    setMsg(getServerError(errorCode, ...parameters));
    return true;
  } else {
    setMsg(getServerError(errorCode, ""));
    return true;
  }
};

const fieldInvalidFileError = (
  error: any,
  setMsg: Function,
  fieldNames: any
): boolean => {
  if (fieldNames === null) return false;
  if (!error.response.data) {
    setMsg(getServerError("S000200_001"));
    return true;
  }
  const data = JSON.parse(new TextDecoder().decode(error.response.data));
  if (!data.key) {
    return false;
  }
  const fe = data as FieldErrorResponse;
  let param = "";
  if (fe.meisaiKey) {
    const meisai = fieldNames[fe.meisaiKey];
    param += `${meisai[fe.meisaiKey]}${fe.meisaiNo}行目`;
    if (fe.koseiKey) {
      const kosei = fieldNames[fe.meisaiKey][fe.koseiKey];
      param += ` ${kosei[fe.koseiKey]}${fe.koseiNo}行目 ${kosei[fe.key]}`;
    } else {
      param += ` ${meisai[fe.key]}`;
    }
  } else {
    param += `${fieldNames[fe.key]}`;
  }
  setMsg(getServerError(fe.errorCode, param));
  return true;
};
const invalidFileError = (error: any, setMsg: Function): boolean => {
  if (!error.response.data) {
    setMsg(getServerError("S000200_001"));
    return true;
  }
  const data = JSON.parse(new TextDecoder().decode(error.response.data));
  if (!data.errorCode) {
    setMsg(getServerError("S000200_001"));
    return true;
  }
  const errorCode = data.errorCode;
  const parameters = data.parameters;
  if (parameters && parameters.length > 0) {
    setMsg(getServerError(errorCode, ...parameters));
    return true;
  } else {
    setMsg(getServerError(errorCode, ""));
    return true;
  }
};
